import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { PostTeeSheetTemplate, IIntervalSection } from "api/rpc/2024-04/facilityAdmin/teesheet/teesheetTemplate";
import { showError } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import TimePick from "components/timePick/TimePick";
import TextField from "components/form/textField/TextField";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import { convertHoles } from "helpers/Helpers";

import "./TeeSheetTemplate.scss";
import Callout from "components/callout/Callout";

const TeeSheetTemplateNew: React.FC = () => {
  const { Option } = Select;
  const history = useHistory();
  const { t } = useTranslation();

  const facilityStore = useAppSelector(store => store.facilityStore);
  const dispatch = useAppDispatch();

  const [templateState, setTemplateState] = useState({
    name: "",
    format: "standard",
    start_time: "06:00",
    end_time: "20:00",
    turn_time: 120,
    holes: 18,
    interval: 8,
    course_id: undefined,
    description: "",
    size: 4,
    interval_sections: [
      {
        start_time: "06:00",
        end_time: "20:00",
        interval_1: 8,
        interval_2: -1,
      },
    ],
  });

  const [warningPopupVisible, setWarningPopupVisible] = useState<boolean>(false);

  const courseOptions = facilityStore.facility?.courses.map((course: any) => {
    return { label: course.full_name, value: course.id };
  });

  async function postTemplate() {
    if (!checkCrossover()) {
      void formatSections();
      const templateRes = await PostTeeSheetTemplate(templateState, true);
      if (templateRes.status !== StatusCode.OK) {
        dispatch(showError(templateRes.data as string));
        return;
      }

      if (templateRes.status === StatusCode.OK) {
        history.push("/admin/settings/tee-sheet/template/" + String(templateRes.data?.id));
        return;
      }
    }

    return;
  }

  function checkCrossover(): boolean {
    const sections = templateState.interval_sections;

    let prevEndTime = "00:00";
    let crossoverExists = false;

    for (let i = 0; i < sections.length; i++) {
      if (sections[i].start_time < prevEndTime) {
        crossoverExists = true;
        setWarningPopupVisible(true);
      }
      prevEndTime = sections[i].end_time;
    }

    return crossoverExists;
  }

  function formatSections() {
    const sections = templateState.interval_sections;

    let prevEndTime = "00:00";

    for (let i = 0; i < sections.length; i++) {
      if (sections[i].interval_2 < 0) {
        sections[i].interval_2 = null;
      }
      if (sections[i].start_time <= prevEndTime) {
        // setWarningPopupVisible(true);
      }
      prevEndTime = sections[i].end_time;
    }

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setTemplateState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDropDownChange(value: any, property: string) {
    setTemplateState(prevState => ({ ...prevState, [property]: value }));
  }

  function addIntervalSection() {
    const sections = templateState.interval_sections;

    sections.push({
      start_time: sections[sections.length - 1].end_time,
      end_time: "20:00",
      interval_1: 8,
      interval_2: null,
    });

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function removeIntervalSection(index: number) {
    const sections = templateState.interval_sections;

    sections.splice(index, 1);

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function handleSectionStartTime(value: string, index: number) {
    const sections = templateState.interval_sections;

    sections[index].start_time = value;

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function handleSectionEndTime(value: string, index: number) {
    const sections = templateState.interval_sections;

    sections[index].end_time = value;

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function handleSectionInterval1(value: number, index: number) {
    const sections = templateState.interval_sections;

    sections[index].interval_1 = value;

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  function handleSectionInterval2(value: number, index: number) {
    const sections = templateState.interval_sections;

    sections[index].interval_2 = value;

    setTemplateState(prevState => ({ ...prevState, interval_sections: sections }));
  }

  const holeOptions = [
    { label: `${convertHoles(18, templateState.course_id ? facilityStore.facility?.courses?.find(course => course.id === templateState.course_id) : facilityStore.facility?.courses[0])} ${t("Holes")}`, value: 18 },
    { label: `${convertHoles(9, templateState.course_id ? facilityStore.facility?.courses?.find(course => course.id === templateState.course_id) : facilityStore.facility?.courses[0])} ${t("Holes")}`, value: 9 },
    ...(facilityStore.facility?.courses?.find(course => course.holes === 27) ? [{ label: t("27 Holes"), value: 27 }] : []),
  ];

  const intervalOptions = [
    { label: t("7 mins"), value: 7 },
    //{ label: t("7.5 mins"), value: 7.5 },
    { label: t("8 mins"), value: 8 },
    //{ label: t("8.5 mins"), value: 8.5 },
    { label: t("9 mins"), value: 9 },
    //{ label: t("9.5 mins"), value: 9.5 },
    { label: t("10 mins"), value: 10 },
    //{ label: t("10.5 mins"), value: 10.5 },
    { label: t("11 mins"), value: 11 },
    //{ label: t("11.5 mins"), value: 11.5 },
    { label: t("12 mins"), value: 12 },
    { label: t("13 mins"), value: 13 },
    { label: t("14 mins"), value: 14 },
    { label: t("15 mins"), value: 15 },
    { label: t("16 mins"), value: 16 },
    { label: t("17 mins"), value: 17 },
    { label: t("18 mins"), value: 18 },
    { label: t("19 mins"), value: 19 },
    { label: t("20 mins"), value: 20 },
  ];

  const sizeOptions = [
    { label: t("4"), value: 4 },
    { label: t("5"), value: 5 },
    { label: t("6"), value: 6 },

  ];

  const primaryAction = {
    content: t("Create"),
    action: () => postTemplate(),
  };

  return (
    <Page
      title={t("New Tee Sheet Template")}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to templates"),
          url: "/admin/settings/tee-sheet/template",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={templateState.name || ""}
                  label={t("Name")}
                  id="name"
                  onChange={handleInputChange}
                  placeholder={t("Template")}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  value={templateState.turn_time || ""}
                  label={t("Turn Time")}
                  id="turn_time"
                  onChange={handleInputChange}
                  placeholder={t("Turn Time")}
                  suffix="minutes"
                  type="number"
                />
                <Select
                  defaultValue={templateState.holes}
                  label={t("Number of Holes")}
                  onChange={(value: any) => handleDropDownChange(value, "holes")}
                >
                  {holeOptions?.map((holeOption: any, index: number) => {
                    return (
                      <Option key={index} name={holeOption.label} value={holeOption.value}>
                        <span>{holeOption.label}</span>
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  defaultValue={templateState.size}
                  label={t("Tee Time Group Size")}
                  onChange={(value: any) => handleDropDownChange(value, "size")}
                >
                  {sizeOptions?.map((sizeOption: any, index: number) => {
                    return (
                      <Option key={index} name={sizeOption.label} value={sizeOption.value}>
                        <span>{sizeOption.label}</span>
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                {courseOptions?.length > 1 && (
                  <Select
                    defaultValue={templateState.course_id}
                    label={t("Course")}
                    onChange={(value: any) => handleDropDownChange(value, "course_id")}
                  >
                    {courseOptions?.map((courseOption: any, index: number) => {
                      return (
                        <Option key={index} name={courseOption.label} value={courseOption.value}>
                          <span>{courseOption.label}</span>
                        </Option>
                      );
                    })}
                  </Select>
                )}
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  value={templateState.description}
                  label={t("Description")}
                  id="description"
                  onChange={handleInputChange}
                  placeholder={t("Template description")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
        <Card.Section
          title={t("Interval Sections")}
          sectionTitleActions={[
            {
              action: () => addIntervalSection(),
              content: t("Add Section"),
            },
          ]}
        >
          {templateState.interval_sections?.map((section: IIntervalSection, index: number) => (
            <div key={index} className="interval-section" style={{border: templateState.interval_sections?.length === 1 || index + 1 === templateState.interval_sections?.length ? "none" : undefined}}>
              <div className="flex justify-between">
                <span className="interval-section-title">
                  {`Time Frame #${index + 1}`} {/* TODO: Translation */}
                </span>
                {templateState.interval_sections.length > 1 && (
                  <Button onClick={() => removeIntervalSection(index)} type="tertiary" size="small">
                    {t("Remove")}
                  </Button>
                )}
              </div>
              <div>
                <FormLayout>
                  <FormLayout.Group>
                    <TimePick
                      value={section.start_time}
                      onChange={timeString => handleSectionStartTime(timeString, index)}
                      label={t("Start Time")}
                      size="large"
                      status={section.start_time === undefined ? "warning" : null}
                      toolTipText={t("Sets the first tee time for this template. This time cannot be changed after the template is created")}
                    />
                    <TimePick
                      value={section.end_time}
                      onChange={timeString => handleSectionEndTime(timeString, index)}
                      label={t("End Time")}
                      size="large"
                      status={section.end_time === undefined ? "warning" : null}
                      toolTipText={t("Sets the last possible tee time for this template. This time cannot be changed after the template is created")}
                    />
                    <Select
                      defaultValue={section.interval_1}
                      label={t("Interval 1")}
                      onChange={(value: number) => handleSectionInterval1(value, index)}
                    >
                      {intervalOptions?.map((intervalOption: any, index: number) => {
                        return (
                          <Option key={index} name={intervalOption.label} value={intervalOption.value}>
                            <span>{intervalOption.value}</span>
                          </Option>
                        );
                      })}
                    </Select>
                    <Select
                      defaultValue={section.interval_2}
                      label={t("Interval 2")}
                      onChange={(value: number) => handleSectionInterval2(value, index)}
                    >
                      <Option key={-1} name={"None"} value={-1}>
                        <span>{t("None")}</span>
                      </Option>
                      {intervalOptions?.map((intervalOption: any, index: number) => {
                        return (
                          <Option key={index} name={intervalOption.label} value={intervalOption.value}>
                            <span>{intervalOption.value}</span>
                          </Option>
                        );
                      })}
                    </Select>
                  </FormLayout.Group>
                </FormLayout>
              </div>
            </div>
          ))}
          <FormLayout>
            <FormLayout.Group>
              <Callout 
              type={"primary"} 
              title={t("Tee On Tip")} 
              actions={{
                primary: {
                  label: t("Hours of Operation"),
                  onClick: () => window.open("/admin/settings/tee-sheet/open-windows", "__blank"),
                },
              }}
              content={<>{t("Choose the widest possible range of tee times for this template. For example, think peak season — if tee times may run from 6:00 AM to 7:00 PM during that time, set those as your start and end times.")} {t("Then, use")} <b>{t("Hours of Operation")}</b> {t("to shorten availability as needed throughout the season.")}</>}/>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Popup
        open={warningPopupVisible}
        type="error"
        title={t("Time Frame Error")}
        description={t("One or more time frame crosses over with another.")}
        onOk={() => setWarningPopupVisible(false)}
        okText={t("Continue")}
      />
    </Page>
  );
};

export default TeeSheetTemplateNew;
